import { useEffect, useMemo, useRef } from 'react'
import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { z } from 'zod'
import { useAppDispatch, useAppSelector } from '@/store/store-hooks'
import { UserFoldersActions } from '../store/slice'
import {
  isExternalStorageSyncedFolder,
  selectFolderDialogType,
  selectCurrentFolderId,
  selectCurrentFolderChildren,
  selectCurrentFolder,
} from '../store/selectors'
import { kMaxFolderNameLength } from '../constants'
import { openGlobalToast } from '@/store/slices/global-toast.slice'
import { GlobalToastType } from '@/constants/constants-ui'
import InputFieldFloatingLabel from '@/components/input-field-floating-label/InputFieldFloatingLabel'
import { useCreateFolder } from '../hooks/useCreateFolder'
import { hasInvalidUserFilesNameCharacter, kInvalidUserFilesNameMessage } from '@/user_files/utils/name-validation'
import { Modal, ModalContent } from '@/components/modal/Modal'
import { ModalFooterWithActions } from '@/components/modal/ModalFooterWithActions'

function buildSchema(occupiedNames: ReadonlySet<string>) {
  return z.object({
    name: z
      .string()
      .trim()
      .min(1, 'Folder name is required')
      .max(kMaxFolderNameLength, `Folder name must be ${kMaxFolderNameLength} characters or less`)
      .refine((val) => !hasInvalidUserFilesNameCharacter(val), kInvalidUserFilesNameMessage)
      .refine((val) => !occupiedNames.has(val), 'A folder with this name already exists in this folder'),
  })
}

type CreateFolderForm = { name: string }

/**
 * CreateFolderDialog
 *
 * Dialog for creating a new folder in the current folder.
 * Uses useCreateFolder hook for direct API calls with proper async handling.
 */
export function CreateFolderDialog() {
  const dispatch = useAppDispatch()
  const formRef = useRef<HTMLFormElement>(null)
  const dialogType = useAppSelector(selectFolderDialogType)
  const currentFolderId = useAppSelector(selectCurrentFolderId)
  const currentFolder = useAppSelector(selectCurrentFolder)
  const folders = useAppSelector(selectCurrentFolderChildren)
  // exclude DMS folders from the existing folder names
  const occupiedNames = useMemo(() => new Set(folders.filter((folder) => !isExternalStorageSyncedFolder(folder)).map((f) => f.name)), [folders])
  const schema = useMemo(() => buildSchema(occupiedNames), [occupiedNames])

  const isOpen = dialogType === 'create'

  // Use hook for folder creation with local state
  const { createFolder, isLoading: isSubmitting, error: submitError, reset: resetCreateFolder } = useCreateFolder()

  const {
    register,
    handleSubmit,
    watch,
    formState: { errors },
    reset,
  } = useForm<CreateFolderForm>({
    resolver: zodResolver(schema),
    mode: 'onSubmit',
    defaultValues: { name: '' },
  })

  // Only disable Create while the field is empty; validation errors surface on submit
  const isNameEmpty = !watch('name').trim()

  // Reset form when dialog opens
  useEffect(() => {
    if (isOpen) {
      reset({ name: '' })
      resetCreateFolder()
    }
  }, [isOpen, reset, resetCreateFolder])

  const handleClose = () => {
    dispatch(UserFoldersActions.closeDialog())
  }

  const onSubmit = async (data: CreateFolderForm) => {
    if (!currentFolderId) {
      return
    }

    const folder = await createFolder(currentFolder ?? currentFolderId, data.name)

    if (folder) {
      // Show success toast and close dialog only on successful creation
      dispatch(openGlobalToast({ type: GlobalToastType.SUCCESS, message: `Created folder: ${folder.name}`, durationMs: 3000 }))
      handleClose()
    }
    // Error is handled by the hook and displayed via submitError
  }

  return (
    <Modal open={isOpen} onOpenChange={handleClose}>
      <ModalContent
        title="New Folder"
        width="md"
        height="max-md"
        footer={
          <ModalFooterWithActions
            secondaryAction={{ label: 'Cancel', onClick: handleClose }}
            primaryAction={{
              label: 'Create',
              onClick: () => formRef.current?.requestSubmit(),
              loading: isSubmitting,
              disabled: isNameEmpty,
            }}
            inlineMessage={submitError ? { variant: 'error', children: submitError } : undefined}
          />
        }
      >
        <form ref={formRef} onSubmit={handleSubmit(onSubmit)}>
          <InputFieldFloatingLabel
            id="folder-name"
            label="Folder Name"
            required
            error={errors.name?.message}
            registration={register('name')}
            inputProps={{ autoFocus: true }}
          />
        </form>
      </ModalContent>
    </Modal>
  )
}
